import { Port, useAISMapContext } from '../Map/AISMapContext';
import React, { useEffect, useMemo, useState } from 'react';
import { useLocalization } from '@ais-ui/locale-management';
import { cn, isNonEmpty } from '@ais-ui/utils';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Input,
  LoadingSpinner,
  ScrollArea,
  Separator,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  useToast,
} from '@ais-ui/ui-core';
import { useDateTimeFormat } from '../conversions/useDateFormat';
import {
  NextPortArrivalWithDate,
  useGetShipByImo,
  useGetShipByImoFunction,
  useNextportPortGet,
} from '@ais-ui/api';
import { RouteButton } from '../utils/RouteButton';
import { PiMagnifyingGlassPlus, PiSortAscending } from 'react-icons/pi';
import { useDebounce } from '@ais-ui/custom-hooks';
import { VesselResult } from '@ais-ui/api-spec/shipdb';
import { ErrorDisplay } from '../../components/ErrorDisplay';
import {
  extractTargetInfos,
  getTargetInfoByMMSI,
  ShipInfo,
  TargetTypeEnum,
  useSession,
} from '@ais-ui/websockets';
import { usePermissions } from '../Permissions/PermissionsProvider';

// Sort types for the vessel arrivals
type SortType = 'eta' | 'probability';

const capitalizeName = (name: string | null | undefined) => {
  if (!name) return '';

  return name
    .toLowerCase()
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

interface PortDetailsPanelProps {
  port: Port;
}

export const PortDetailsPanel = ({ port }: PortDetailsPanelProps) => {
  const [search, setSearch] = useState<string>('');
  const bouncedSearchString = useDebounce(search, 200);
  const [sortType, setSortType] = useState<SortType>('eta');
  const { messages } = useLocalization();

  return (
    <div className="p-0 pt-0 flex-1 flex flex-col overflow-hidden space-y-4">
      <div>
        <h3 className="text-lg font-medium">Vessel arrival predictions</h3>
        <Separator className="mt-2"></Separator>
      </div>
      <div className="flex gap-2">
        <Input
          id="search"
          placeholder={'Search for vessel predictions'}
          className="h-10 flex-1"
          value={search}
          onChange={(event) => setSearch(event.currentTarget.value)}
        />
        <DropdownMenu>
          <Tooltip>
            <DropdownMenuTrigger asChild>
              <TooltipTrigger asChild>
                <Button variant="outline" size="icon" className="h-10 w-10">
                  <PiSortAscending className="h-5 w-5" />
                </Button>
              </TooltipTrigger>
            </DropdownMenuTrigger>
            <TooltipContent>Sort vessels</TooltipContent>
          </Tooltip>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => setSortType('eta')}>
              Sort by arrival time
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setSortType('probability')}>
              Sort by prediction percentage
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <ArrivingVessels
        port={port}
        bouncedSearchString={bouncedSearchString}
        sortType={sortType}
      ></ArrivingVessels>
    </div>
  );
};

interface IdentificationItemProps {
  label: string;
  value: string;
}

const IdentificationItem = ({ label, value }: IdentificationItemProps) => {
  return (
    <div className="flex flex-col gap-1">
      <p className="text-sm font-medium flex-none">{label}</p>
      <p className="text-sm text-gray-500 dark:text-gray-400 flex-grow text-ellipsis overflow-hidden">
        {isNonEmpty(value) ? value : 'Unknown'}
      </p>
    </div>
  );
};

interface IdentificationProps {
  port: Port;
}

export const PortIdentification = ({ port }: IdentificationProps) => {
  const { messages } = useLocalization();

  return (
    <div className="grid grid-cols-2 gap-4">
      <IdentificationItem
        label={messages.portDetailsBaseNameLabel}
        value={port.name}
      />
      <IdentificationItem
        label={messages.portDetailsBaseLocodeLabel}
        value={port.locode}
      />
      <IdentificationItem
        label={messages.portDetailsBaseCountryLabel}
        value={port.country}
      />
    </div>
  );
};

interface ArrivingVesselsProps {
  port: Port;
  bouncedSearchString: string;
  sortType: SortType;
}

interface ArrivingVesselsFallBackProps {
  error: any;
}

const ArrivingVesselsFallBack = ({ error }: ArrivingVesselsFallBackProps) => {
  if (error?.response?.parsedBody?.code === 422) {
    return (
      <ErrorDisplay
        srcImage="/422_not_supported_error_bro.png"
        message="Vessel arrival predictions not supported for Port"
        className="h-full pb-40"
      />
    );
  }

  return (
    <ErrorDisplay
      message="Couldn't fetch vessel arrival predictions for the port"
      className="h-full pb-40"
    />
  );
};

const ArrivingVessels = ({
  port,
  bouncedSearchString,
  sortType,
}: ArrivingVesselsProps) => {
  const { messages } = useLocalization();

  const { getShipByImo } = useGetShipByImoFunction();
  const [ships, setShips] = useState<VesselResult[]>([]);
  const [isLoadingShips, setIsLoadingShips] = useState(false);
  const [hasFetchedShips, setHasFetchedShips] = useState(false);
  const [filteredArrivals, setFilteredArrivals] = useState<
    NextPortArrivalWithDate[]
  >([]);

  const {
    data,
    isLoading: isLoadingNextport,
    isError,
    error,
  } = useNextportPortGet(port.locode);

  useEffect(() => {
    if (!data?.records || hasFetchedShips) return;

    const fetchAllShips = async () => {
      setIsLoadingShips(true);
      const shipPromises = data.records.map(async (arrival) => {
        const ship = await getShipByImo(arrival.imo);
        return ship;
      });

      const isVesselResult = (
        ship: VesselResult | null
      ): ship is VesselResult => ship !== null;

      const results = await Promise.all(shipPromises);

      setShips(results.filter(isVesselResult));

      setIsLoadingShips(false);
      setHasFetchedShips(true);
    };

    fetchAllShips();
  }, [data?.records, getShipByImo, hasFetchedShips]);

  const isLoading = useMemo(() => {
    if (isLoadingNextport || isLoadingShips) {
      return true;
    }

    return false;
  }, [isLoadingNextport, isLoadingShips]);

  useEffect(() => {
    if (!bouncedSearchString) {
      // If no search string, include all arrivals
      setFilteredArrivals(data?.records || []);
      return;
    }

    const searchLower = bouncedSearchString.toLowerCase();

    // Filter ships
    const filteredShips = ships.filter(
      (ship) =>
        ship?.basic?.vesselName?.toLowerCase().includes(searchLower) ||
        ship?.basic?.vesselMmsiNumber?.toString().includes(searchLower) ||
        ship?.basic?.vesselImoNumber?.toString().includes(searchLower)
    );

    const filteredShipsRecord = filteredShips.reduce<
      Record<number, VesselResult>
    >((acc, ship) => {
      acc[ship?.basic?.vesselImoNumber] = ship;
      return acc;
    }, {});

    // Determine filtered arrivals based on filtered ships
    const filtered = data?.records.filter(
      (arrival) => filteredShipsRecord[arrival.imo] !== undefined
    );

    setFilteredArrivals(filtered || []);
  }, [bouncedSearchString, data?.records, ships]);

  // Make sure we re-sort whenever sortType changes
  const sortedArrivals = useMemo(() => {
    if (!filteredArrivals || filteredArrivals.length === 0) return [];

    const sorted = [...filteredArrivals];

    if (sortType === 'eta') {
      return sorted.sort((a, b) => {
        // Handle cases where eta could be undefined
        if (!a.eta) return 1;
        if (!b.eta) return -1;
        return new Date(a.eta).getTime() - new Date(b.eta).getTime();
      });
    } else if (sortType === 'probability') {
      return sorted.sort((a, b) => {
        // Handle cases where probability is undefined
        const probA = a.probability ?? 0;
        const probB = b.probability ?? 0;
        return probB - probA; // Sort by highest probability first
      });
    }

    return sorted;
  }, [filteredArrivals, sortType]);

  const [showAll, setShowAll] = useState(false);

  const INITIAL_VISIBLE_COUNT = 15;

  const displayedArrivals = useMemo(() => {
    if (!sortedArrivals) return [];
    return showAll
      ? sortedArrivals
      : sortedArrivals.slice(0, INITIAL_VISIBLE_COUNT);
  }, [sortedArrivals, showAll]);

  const onShowAll = () => {
    setShowAll((prevShowAll) => !prevShowAll);
  };

  return (
    <div className="p-0 pt-0 flex-1 flex flex-col overflow-hidden">
      {isLoading && <LoadingSpinner />}
      {isError && <ArrivingVesselsFallBack error={error} />}
      {!isLoading && displayedArrivals.length > 0 && (
        <div className="flex flex-col h-full">
          {filteredArrivals.length > INITIAL_VISIBLE_COUNT && (
            <div className="flex w-full mt-2">
              <Button onClick={onShowAll} variant="ghost" className="">
                {showAll
                  ? 'Show first fifteen (15) predictions'
                  : `Show all predictions (${filteredArrivals.length})`}
              </Button>
            </div>
          )}
          <ScrollArea className="flex-grow overflow-y-auto border rounded-lg px-2">
            {displayedArrivals.map((arrival) => (
              <ArrivingVesselItem
                key={arrival.imo}
                arrival={arrival}
                port={port}
              />
            ))}
          </ScrollArea>
        </div>
      )}
    </div>
  );
};

interface ArrivingVesselProps {
  arrival: NextPortArrivalWithDate;
  port: Port;
}

const ArrivingVesselItem = ({ arrival, port }: ArrivingVesselProps) => {
  const { formatDateTimeWithTimeZone } = useDateTimeFormat();
  const { toast } = useToast();
  const { olSession } = useSession();
  const permissions = usePermissions();

  const hasShipDbPermissions =
    permissions.mdmAdvancedVesselInfo ||
    permissions.mdmRegister ||
    permissions.mdmIntegrity;

  const { setZoomToShip } = useAISMapContext();
  const { data: ship, isLoading } = useGetShipByImo(
    arrival.imo,
    hasShipDbPermissions
  );
  const { messages } = useLocalization();

  const isShipPositionKnown = useMemo(() => {
    if (!ship?.basic?.lastKnownPosition) {
      return false;
    }

    const { latitude, longitude } = ship.basic.lastKnownPosition;

    // Check if both latitude and longitude are valid numbers
    const isPositionValid =
      typeof latitude === 'number' &&
      typeof longitude === 'number' &&
      !isNaN(latitude) &&
      !isNaN(longitude);

    if (!isPositionValid) {
      return false;
    }

    return true;
  }, [ship]);

  const onZoomToShip = async () => {
    if (!ship?.basic?.vesselMmsiNumber || !isShipPositionKnown) {
      return;
    }

    const result = await getTargetInfoByMMSI(
      olSession,
      ship.basic.vesselMmsiNumber
    );

    const extractedTargetInfo = extractTargetInfos(result);

    if (
      extractedTargetInfo.extractedTargetInfoType !== TargetTypeEnum.Ship ||
      extractedTargetInfo.extractedTargetInfo.length === 0
    ) {
      toast({
        title: messages.genericToastTitleError,
        description: 'Could not find ship',
        variant: 'destructive',
      });
      return;
    }

    const aisShip = {
      callSign: (extractedTargetInfo.extractedTargetInfo[0] as ShipInfo)
        .callSign,
      imo: (extractedTargetInfo.extractedTargetInfo[0] as ShipInfo).imo,
      mmsi: (extractedTargetInfo.extractedTargetInfo[0] as ShipInfo).mmsi,
      name: (extractedTargetInfo.extractedTargetInfo[0] as ShipInfo).name,
      type: 'TT_SHIP',
    };

    setZoomToShip(aisShip);
  };

  const shipName = useMemo(() => {
    return capitalizeName(ship?.basic?.vesselName);
  }, [ship]);

  return (
    <div className="w-full flex space-x-4 py-2">
      <div className="flex space-x-2 items-center">
        <RouteButton
          mmsi={ship?.basic?.vesselMmsiNumber}
          shipName={shipName}
          nextport={arrival}
          portName={port?.name}
        ></RouteButton>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              size="icon"
              variant="ghost"
              onClick={onZoomToShip}
              disabled={!isShipPositionKnown}
            >
              <PiMagnifyingGlassPlus className="h-6 w-6"></PiMagnifyingGlassPlus>
            </Button>
          </TooltipTrigger>
          <TooltipContent>Zoom to ship</TooltipContent>
        </Tooltip>
        <Separator orientation="vertical" className="h-14 bg-gray-300" />
      </div>
      <div>
        <div className="flex flex-col space-y-1.5 justify-center">
          <div className="flex space-x-2 items-center">
            <p className="text-sm text-left font-medium leading-none group-hover:underline">
              {shipName ? shipName : 'Unknown'}
            </p>
          </div>
          <div className="flex space-x-2 items-center">
            <p className="text-sm text-left text-muted-foreground leading-none group-hover:underline">
              MMSI:{' '}
              {ship?.basic?.vesselMmsiNumber
                ? ship.basic.vesselMmsiNumber
                : 'Unknown'}
            </p>
            <Separator orientation="vertical" className="h-4 bg-gray-300" />
            <p className="text-sm text-left text-muted-foreground leading-none group-hover:underline">
              IMO:{' '}
              {ship?.basic?.vesselImoNumber
                ? ship.basic.vesselImoNumber
                : 'Unknown'}
            </p>
          </div>
          <div className="flex space-x-2 items-center">
            <Tooltip>
              <TooltipTrigger>
                <span
                  className={cn(
                    'block w-2 h-2 rounded-full',
                    (arrival.probability ?? -1) === -1 && 'bg-gray-500',
                    (arrival.probability ?? -1) >= 0.5 && 'bg-green-500',
                    (arrival.probability ?? -1) < 0.5 &&
                      (arrival.probability ?? -1) > 0.2 &&
                      'bg-yellow-500',
                    (arrival.probability ?? -1) <= 0.2 && 'bg-red-500'
                  )}
                ></span>
              </TooltipTrigger>
              <TooltipContent>
                The probability that {ship?.basic?.vesselName} will go to{' '}
                {port.name}
              </TooltipContent>
            </Tooltip>
            <p className="text-sm text-left text-muted-foreground">
              {arrival.probability
                ? `${(arrival.probability * 100).toFixed(1)} %`
                : 'Unknown'}
            </p>
            <Separator orientation="vertical" className="h-4 bg-gray-300" />
            <p className="text-sm text-left text-muted-foreground">
              {formatDateTimeWithTimeZone(arrival.eta, true)}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
